import { useState, useEffect, Fragment } from 'react'
import { menu, isGroup, isPathActive, activeNavValue, activeNavLabel } from 'config/menu'
import { NavLink } from 'react-router-dom'
import Tabs, { tabsClasses } from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListSubheader from '@mui/material/ListSubheader'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useMediaQuery } from '@mui/material'
import { useLocation } from 'react-router-dom'

import BurgerButton from './BurgerButton/BurgerButton'
import s from './Header.module.css'

const DropDownMenu = ({ pathname }) => {
  const [menuOpened, setMenuOpened] = useState(false)

  useEffect(() => {
    setMenuOpened(false)
  }, [pathname])

  // The burger list has no room for a second level of disclosure — a submenu
  // inside an already-collapsed menu is two taps to reach one screen. Groups are
  // flattened under a heading instead: everything stays one tap away, and the
  // grouping still reads.
  const renderLeaf = (item, extraClass = '') => (
    <ListItemButton
      className={`${s.dropdownItem} ${extraClass}`}
      component={NavLink}
      selected={isPathActive(item.path, pathname)}
      to={item.path}
      id={item.id}
      label={item.name}
      value={item.path}
    >
      {item.name}
    </ListItemButton>
  )

  return (
    <div className={s.dropdownContainer}>
      <BurgerButton
        opened={menuOpened}
        onClick={() => setMenuOpened(!menuOpened)}
        label={activeNavLabel(pathname)}
      />

      <div
        className={s.dropdown}
        style={{
          display: menuOpened ? 'block' : 'none',
        }}
      >
        <List>
          {menu.map((item, i) => (
            <Fragment key={item.id}>
              {isGroup(item) ? (
                <>
                  <ListSubheader className={s.dropdownGroupLabel} disableSticky>
                    {item.name}
                  </ListSubheader>
                  {item.children.map(child => (
                    <Fragment key={child.id}>{renderLeaf(child, s.dropdownChildItem)}</Fragment>
                  ))}
                </>
              ) : (
                renderLeaf(item)
              )}
              {i !== menu.length - 1 && <Divider />}
            </Fragment>
          ))}
        </List>
      </div>
    </div>
  )
}

export const Nav = () => {
  const { pathname } = useLocation();
  const isMobile = useMediaQuery('(max-width: 1000px)')
  // { id, anchorEl } of the open group, or null. Keyed by id rather than a bare
  // boolean so a second group can be added without touching this.
  const [openGroup, setOpenGroup] = useState(null)

  // Navigating away must close the menu. The click that navigates is inside the
  // menu, so without this it stays open over the page it just left.
  useEffect(() => {
    setOpenGroup(null)
  }, [pathname])

  if (isMobile) {
    return (
      <DropDownMenu pathname={pathname} />
    )
  }

  return (
    <>
      <Tabs
        value={activeNavValue(pathname)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          [`& .${tabsClasses.scrollButtons}`]: {
            '&.Mui-disabled': { opacity: 0.3 },
          },
        }}
      >
        {menu.map(item => isGroup(item)
          // A group tab is a disclosure control, not a link: no `to`, and it
          // reports itself to assistive tech as opening a menu.
          ? <Tab
            key={item.id}
            id={item.id}
            value={item.id}
            // The chevron goes INSIDE the label, not in Tab's `icon` prop. That
            // prop grows the tab's line box by a pixel, which lifts the header
            // from 48px to 49 and shifts every page below it down — a one-pixel
            // diff on all 33 visual baselines for a decoration. Inline, with the
            // glyph clipped to the text's own line box, it costs nothing.
            label={
              <span className={s.groupTabLabel}>
                {item.name}
                <ExpandMoreIcon className={s.groupTabChevron} />
              </span>
            }
            aria-haspopup="menu"
            aria-expanded={openGroup?.id === item.id}
            aria-controls={openGroup?.id === item.id ? `${item.id}_menu` : undefined}
            onClick={event => setOpenGroup({ id: item.id, anchorEl: event.currentTarget })}
          />
          : <Tab
            component={NavLink}
            to={item.path}
            key={item.id}
            id={item.id}
            label={item.name}
            value={item.path}
          />
        )}
      </Tabs>

      {menu.filter(isGroup).map(group => (
        <Menu
          key={group.id}
          id={`${group.id}_menu`}
          anchorEl={openGroup?.anchorEl}
          open={openGroup?.id === group.id}
          onClose={() => setOpenGroup(null)}
          MenuListProps={{ 'aria-labelledby': group.id }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {group.children.map(child => (
            <MenuItem
              key={child.id}
              id={child.id}
              component={NavLink}
              to={child.path}
              selected={isPathActive(child.path, pathname)}
              onClick={() => setOpenGroup(null)}
            >
              {child.name}
            </MenuItem>
          ))}
        </Menu>
      ))}
    </>
  )
}

export default Nav
